/**
 * Shared pure-function helpers used by the shell sub-reducers.
 * Extracted from editorStore.ts — pure structural refactor.
 *
 * IMPORTANT: This module must NOT import from editorStore.ts to
 * avoid circular dependencies. One-directional: editorStore.ts
 * and all sub-reducers import from here.
 */

import {
  appendCreatedEntity,
  type PropertyMutationResult,
} from "@dxf-ai-editor/command-engine";
// ABB-S5a — the AMBIENT bridge-turn marker, read here to OVERRIDE the audit-ring
// entry's actor + stamp its channel for a remote agent-bridge turn (INV-SOURCE:
// `source` stays `'system'`; the channel is the distinct typed attribution). This
// is the `state → agentBridge` edge — the SAME allowed one-directional edge
// `editorStore → agentBridge/inFlightLatch` already uses. The `auditStaging` leaf
// imports NOTHING (no editorStore, no state), so there is NO cycle (verified
// `npm run deps:graph` cycles=0). reducerHelpers still imports no editorStore.
import {
  BRIDGE_AUDIT_ACTOR_EMAIL,
  BRIDGE_AUDIT_CHANNEL,
  isBridgeTurnActive,
} from "../agentBridge/auditStaging";
import {
  affectedEntityIdsForCommand,
  AUDIT_ENTITY_IDS_MAX,
  describeAuditOperation,
  describeViewOpOperation,
  editorShellStateSchema,
  type AuditExtraParams,
  type AuditOperationParams,
  type CommandSource,
  type DynamicBlockCommand,
  type EditorCommand,
  type EditorShellState,
  type EntityPreview,
  type SessionUser,
  type TelemetryTone,
  type UiInteractionAuditParams,
  type ViewOpDetail,
  type ViewOpType,
} from "@dxf-ai-editor/contracts";
import type { DrawingDocument } from "@dxf-ai-editor/drawing-model";

// `nextDenseEntityIdWithBlockSeed` is the one-truth block-namespace-aware
// dense-id algorithm (shared with the geometry reducer's
// `nextGeometryEntityId` + batch allocator) — it ALSO owns the
// BEDIT/REFEDIT live-mirror exclusion internally (N5, local Codex
// round-5/round-6), so `nextEntityId` below just delegates to it.
// `getBlockNamespaceSeed` is the SAME cached block-namespace
// `nextGeometryEntityId` reads (N5, local Codex round-2/round-4) — keeps
// the shell-side minter here in lockstep with it. The helper imports only
// from packages — no cycle (verified `npm run deps:graph` cycles=0);
// reducerHelpers still imports no editorStore.
import {
  getBlockNamespaceSeed,
  nextDenseEntityIdWithBlockSeed,
} from "./geometryReducerHelpers";

export function selectCurrentUser(state: EditorShellState): SessionUser {
  return (
    state.session.users.find(
      (user) => user.email === state.session.currentUserEmail,
    ) ?? state.session.users[0]
  );
}

// Sourced from the contract's `TelemetryTone` (info/success/warning/error) so
// the audit-ring tone domain can never drift from the telemetry tone domain the
// CC-1a transport projects it through (`toneToStatus`). ТЗ §14: the `error`
// tone is the FAILED-command / parse-error signal → `status:"error"`.
type AuditTone = TelemetryTone;

/**
 * CC-1c — single chokepoint that prepends one audit event (+ a transcript
 * row) to the shell state, stamping it with the actor, sequence, the typed
 * `commandType`, and the structured `params` payload the CC-1a transport
 * carries. Both the command-based `pushAuditEvent` and the view-op
 * `pushViewOpAuditEvent` delegate here so the ring shape can never drift
 * between the two entry points.
 */
function appendAuditEvent(
  state: EditorShellState,
  entry: {
    commandType: string;
    source: CommandSource;
    summary: string;
    tone: AuditTone;
    params?: AuditOperationParams;
    /**
     * CC-1b — the AFFECTED entity ids of the command (the per-action audit
     * evidence persisted to `audit_events.entity_ids` via `AuditRecord.entityIds`).
     * Command emitters pass the verbatim list from `affectedEntityIdsForCommand`;
     * view-op / ui-interaction emitters touch no entity and omit it. Stamped on
     * the ring entry ONLY when non-empty so a no-entity event stays lean.
     */
    entityIds?: string[];
    /**
     * CC-1e — when true the audit event is a NON-command UI interaction
     * (click / toggle / nav, the ТЗ §14 "clicks" axis). Such an event:
     *   1. is NOT mirrored into `commandLine.transcript` (no command-feed
     *      spam — pure observability), and
     *   2. (P2-1 fix) is appended to the SEPARATE, independently-bounded
     *      `audit.uiEvents` queue rather than the visible, cap-bounded
     *      `audit.events` ring. Routing ui/* into the visible ring let a burst
     *      of hidden telemetry (rapid ribbon-tab / ORTHO / OSNAP toggling)
     *      evict real command / view-op entries out of the ring → DATA LOSS in
     *      the History panel. The CC-1a transport observes BOTH queues, so the
     *      ui/* events still reach the server channel; only the VISIBLE ring is
     *      spared.
     * Default false (command + view-op keep the visible-ring + transcript-row
     * history-panel parity they have always had).
     */
    uiInteraction?: boolean;
  },
): EditorShellState {
  const currentUser = selectCurrentUser(state);
  const sequence = state.audit.nextSequence;

  // ABB-S5a — when a remote agent-bridge turn is ambiently active (S4 set the
  // marker around the synchronous safe-contour call), OVERRIDE the actor to the
  // synthetic bridge actor + STAMP `channel:'agent-bridge'` on the RING entry (the
  // typed model state, not a push-only cosmetic). INV-SOURCE: `source` stays
  // whatever the command carried (`'system'` for a bridge turn) — the channel is
  // the distinct attribution, never a widening of the closed `commandSourceSchema`.
  // No bridge turn → the actor is the current user + NO channel (verbatim current
  // behaviour; `channel` is an OPTIONAL contract field so the lean entry validates).
  const bridgeTurn = isBridgeTurnActive();

  const auditEntry = {
    id: `audit-${sequence}`,
    sequence,
    commandType: entry.commandType,
    source: entry.source,
    actorEmail: bridgeTurn ? BRIDGE_AUDIT_ACTOR_EMAIL : currentUser.email,
    ...(bridgeTurn ? { channel: BRIDGE_AUDIT_CHANNEL } : {}),
    summary: entry.summary,
    // #353 @codex F7 — persist the outcome tone ON the ring entry. For
    // command / view-op events the tone is ALSO mirrored onto the transcript
    // row, but the ТЗ §14 "clicks" family (ui-click / ui-toggle / ui-nav) is
    // kept OUT of the transcript, so the CC-1a transport — which previously
    // recovered tone only from `tx-${sequence}` transcript rows — defaulted a
    // ui-* error tone to "info" and posted it as status:"ok". Persisting the
    // tone here lets the transport recover it for the transcript-less family.
    tone: entry.tone,
    ...(entry.params ? { params: entry.params } : {}),
    // CC-1b — stamp the affected-entity id list (the audit-record evidence) only
    // when the command touched at least one entity; a lean no-entity event stays
    // free of the field (it validates as OPTIONAL) and the audit projection falls
    // back to the single `params.entityId` for any pre-slice snapshot.
    //
    // #479 F-ringcap — CAP the list at the ring boundary to `AUDIT_ENTITY_IDS_MAX`
    // (the SAME cap the wire projection `resolveAffectedEntityIds` applies at POST
    // time). This is the single by-construction choke point every audit push funnels
    // through (`pushAuditEvent` / `pushViewOpAuditEvent` / `pushUiInteractionAudit-
    // Event` all call `appendAuditEvent`), so a SELECT-ALL + CHPROP over a large
    // drawing can no longer copy — and `editorShellStateSchema.parse` — tens of
    // thousands of ids into the in-memory ring only to have the wire projection
    // discard everything past the first `AUDIT_ENTITY_IDS_MAX`. Slicing here loses
    // NO wire data (the POST already truncates to the same bound) while bounding the
    // per-command copy/validation/memory cost the ring pays on every command.
    ...(entry.entityIds && entry.entityIds.length > 0
      ? {
          entityIds:
            entry.entityIds.length > AUDIT_ENTITY_IDS_MAX
              ? entry.entityIds.slice(0, AUDIT_ENTITY_IDS_MAX)
              : entry.entityIds,
        }
      : {}),
  };

  return editorShellStateSchema.parse({
    ...state,
    commandLine: entry.uiInteraction
      ? state.commandLine
      : {
          ...state.commandLine,
          transcript: [
            {
              id: `tx-${sequence}`,
              tone: entry.tone,
              message: entry.summary,
            },
            ...state.commandLine.transcript,
          ].slice(0, 24),
        },
    audit: {
      nextSequence: sequence + 1,
      // P2-1 — command / view-op events keep the visible ring; ui/* events go
      // to the separate `uiEvents` queue and never touch (or evict from) it.
      events: entry.uiInteraction
        ? state.audit.events
        : [auditEntry, ...state.audit.events].slice(0, 24),
      uiEvents: entry.uiInteraction
        ? [auditEntry, ...(state.audit.uiEvents ?? [])].slice(0, 24)
        : state.audit.uiEvents,
    },
  });
}

export function pushAuditEvent(
  state: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
  summary: string,
  tone: AuditTone,
  // CC-1c — typed runtime-context extras (e.g. undo/redo reversed type + head
  // index). Typed as `AuditExtraParams` so it can NEVER carry the
  // `category`/`op` discriminants, and the command-derived base is spread LAST
  // so even a force-cast extra cannot override them (defence in depth).
  extraParams?: AuditExtraParams,
  // CC-1b R5 (@codex) — the AFFECTED entity ids when the REDUCER, not the source
  // command, owns them. A geometry create (geometry/create-line, -circle, …)
  // allocates `created.entity.id` inside the reducer, so the id is absent from
  // the command and `affectedEntityIdsForCommand` cannot recover it (returns []).
  // The create arms pass the freshly-minted id(s) here so the persisted audit
  // row links the creation event to the created object. When OMITTED the ids are
  // derived from the command verbatim, exactly as before (every modify / select /
  // view-op path is unchanged).
  reducerAllocatedEntityIds?: readonly string[],
): EditorShellState {
  const params: AuditOperationParams = {
    ...(extraParams ?? {}),
    ...describeAuditOperation(command),
  };
  // CC-1b — capture the AFFECTED entity ids: the reducer-allocated ids when the
  // caller supplied them (create commands), else verbatim from the source
  // command so a multi-entity op (selection / CHPROP / properties-apply /
  // geometry-modify) persists its real ids, not just the aggregate
  // `params.entityCount`. Empty ids are filtered (mirrors the command-derived
  // `asIdList` min-length-1 rule) so a stray "" never reaches the audit row.
  const entityIds =
    reducerAllocatedEntityIds !== undefined
      ? reducerAllocatedEntityIds.filter((id) => id.length > 0)
      : affectedEntityIdsForCommand(command);
  return appendAuditEvent(state, {
    commandType: command.type,
    source: command.source,
    summary,
    tone,
    params,
    entityIds,
  });
}

/**
 * CC-1c — emit a typed `view-op` audit event for an operation that does NOT
 * pass through the command schema / `geometryShellReducer` (REGEN/REGENALL/
 * ZOOM/PAN). The `opType` is stored as the audit `commandType` so the CC-1a
 * transport's `classifyTelemetryKind` tags it `kind:"view-op"`.
 */
export function pushViewOpAuditEvent(
  state: EditorShellState,
  opType: ViewOpType,
  source: CommandSource,
  summary: string,
  tone: AuditTone,
  detail?: ViewOpDetail,
): EditorShellState {
  return appendAuditEvent(state, {
    commandType: opType,
    source,
    summary,
    tone,
    params: describeViewOpOperation(opType, detail),
  });
}

/**
 * CC-1e — emit a typed NON-command interaction audit event (the ТЗ §14
 * "clicks" axis: UI clicks, drawing-aid toggles, panel/tab/space navigation).
 * The typed `params` (built by `describeUiClickOperation` /
 * `describeUiToggleOperation` / `describeUiNavOperation`) carries both the
 * grouping discriminants (`category`/`op`) and the op-specific fields. The
 * `op` family literal is stored as the audit `commandType` so the ring stays
 * valid; the CC-1a transport recovers the precise wire `kind` from
 * `params.category`.
 *
 * Unlike `pushAuditEvent` / `pushViewOpAuditEvent`, this appends to the
 * SEPARATE `audit.uiEvents` queue ONLY (`uiInteraction: true`) — interactions
 * are pure observability and must never spam the user-facing command-line
 * transcript NOR evict real command / view-op entries out of the visible
 * `audit.events` ring (P2-1). The CC-1a transport observes both queues, so the
 * server channel still carries them (no behaviour change to the command feed or
 * the History panel; this keeps CC-1e observability-only).
 */
export function pushUiInteractionAuditEvent(
  state: EditorShellState,
  params: UiInteractionAuditParams,
  source: CommandSource,
  summary: string,
  tone: AuditTone = "info",
): EditorShellState {
  return appendAuditEvent(state, {
    commandType: params.op,
    source,
    summary,
    tone,
    params,
    uiInteraction: true,
  });
}

/**
 * N5 (#524 follow-up, local Codex round-2 P1). This shell-side minter
 * previews an id BEFORE the geometry reducer runs and independently mints
 * the AUTHORITATIVE id via `nextGeometryEntityId` — the two are expected to
 * AGREE so `selection.entityIds` / the audit event (both stamped HERE, from
 * this preview id, before the geometry-driven resync) still name the real
 * created entity after `syncShellStateFromGeometryDocument` rebuilds
 * `state.drawing.entities` from the geometry document (that resync
 * preserves `state.selection` verbatim — it does not re-validate it against
 * the rebuilt entity list).
 *
 * `state.drawing.entities` only mirrors LIVE space content, never
 * `geometryDocument.blocks[*].entities` — so once `nextGeometryEntityId`
 * became block-aware (N5), a bare `state.drawing.entities` scan here could
 * mint a DIFFERENT id than the geometry reducer for the exact same command
 * (e.g. a block parks "line-1", no live space holds a Line: the geometry
 * reducer now correctly mints "line-2", but this scan would still return
 * "line-1") — the preview list gets corrected to "line-2" by the resync,
 * but the stale "line-1" selection/audit event would keep pointing at a
 * NONEXISTENT entity. `geometryDocument` is REQUIRED so this scan covers
 * the identical merged namespace `nextGeometryEntityId` does, keeping the
 * two allocators in lockstep.
 */
export function nextEntityId(
  state: EditorShellState,
  prefix: string,
  geometryDocument: DrawingDocument,
): string {
  // Delegates to the one-truth block-namespace-aware dense-id algorithm
  // (shared with `nextGeometryEntityId` + the batch allocator) over the
  // flat shell-state entity list UNIONED with the SAME cached block
  // namespace `nextGeometryEntityId` reads (N5, local Codex round-4 P1) —
  // no re-flatten/re-scan of `document.blocks` when it hasn't changed since
  // the last mint against this document. The BEDIT/REFEDIT live-mirror
  // exclusion (N5, local Codex round-5/round-6) runs INSIDE
  // `nextDenseEntityIdWithBlockSeed` itself, keyed per-entity on
  // `EntityPreview.spaceId` — no separate filter needed here.
  return nextDenseEntityIdWithBlockSeed(
    state.drawing.entities,
    prefix,
    getBlockNamespaceSeed(geometryDocument),
  );
}

export function summarizePropertyMutation(
  prefix: string,
  result: PropertyMutationResult,
): { summary: string; tone: "success" | "warning" } {
  // fix/properties-apply-derived-fields-skip — the Properties panel
  // resubmits EVERY displayed row on every apply (not just the edited
  // field), so a computed/display-only row (Line Length/Angle, Circle
  // Diameter/Circumference/Area, Arc Total angle/Arc length, LWPOLYLINE
  // Length/Area, Dimension Measurement, …) rides along unedited on every
  // single apply and is always rejected by the per-type patchable gate.
  // That is expected — AutoCAD shows these read-only, they were never a
  // settable property to begin with — not a failed write, so it must never
  // count toward (or be implied by) "skipped N incompatible property
  // application(s)". Only a GENUINE incompatibility (a property that does
  // not apply to this entity type, or a real validation failure) counts —
  // `MutationSkip.kind` is the ONE-TRUTH discriminant every skip site sets
  // (`propertyMutations.ts` / `simpleGeometry.ts` /
  // `extendedPropertyMutations.ts`, command-engine).
  const incompatibleCount = result.skipped.filter(
    (skip) => skip.kind === "incompatible",
  ).length;
  const skipSuffix =
    incompatibleCount > 0
      ? `; skipped ${incompatibleCount} incompatible property application(s)`
      : "";

  if (result.updatedEntityIds.length === 0) {
    return {
      summary: `${prefix}: no applicable property changes${skipSuffix}`,
      tone: "warning",
    };
  }

  return {
    summary: `${prefix}: updated ${result.updatedEntityIds.length} object(s)${skipSuffix}`,
    tone: "success",
  };
}

export function applyPropertyResults(
  state: EditorShellState,
  command: EditorCommand,
  results: PropertyMutationResult[],
  prefix: string,
): EditorShellState {
  const mergedResult = results.reduce<PropertyMutationResult>(
    (accumulator, result) => ({
      entities: result.entities,
      updatedEntityIds: [
        ...new Set([
          ...accumulator.updatedEntityIds,
          ...result.updatedEntityIds,
        ]),
      ],
      appliedChanges: [...accumulator.appliedChanges, ...result.appliedChanges],
      skipped: [...accumulator.skipped, ...result.skipped],
      summary: result.summary,
    }),
    {
      entities: state.drawing.entities,
      updatedEntityIds: [],
      appliedChanges: [],
      skipped: [],
      summary: `${prefix}: no applicable property changes`,
    },
  );

  const { summary, tone } = summarizePropertyMutation(prefix, mergedResult);

  return pushAuditEvent(
    {
      ...state,
      drawing: {
        ...state.drawing,
        entities: mergedResult.entities,
      },
    },
    command,
    summary,
    tone,
  );
}

/**
 * Shared entity creation helper: builds the common shell-state shape
 * after a geometry entity is created (sets panel to properties, appends
 * entity, selects it, and emits an audit event).
 * @public
 */
export function applyGeometryCreation(
  state: EditorShellState,
  command: EditorCommand,
  entity: EntityPreview,
  summary: string,
): EditorShellState {
  return pushAuditEvent(
    {
      ...state,
      ui: { activePanel: "properties" },
      drawing: {
        ...state.drawing,
        entities: appendCreatedEntity(state.drawing.entities, entity),
      },
      selection: { entityIds: [entity.id] },
    },
    command,
    summary,
    "success",
  );
}
